import { useId } from "react";
import {
  maskPhoneInput,
  PHONE_LINE_TYPE_OPTIONS,
  type PhoneLineType,
} from "@/lib/phone";
import { FieldMarker } from "@/components/FieldMarker";
import {
  FIELD_CONTROL,
  FIELD_ERROR,
  FIELD_LABEL,
  FIELD_LABEL_ERROR,
} from "@/components/fieldStyles";

// Labeled phone input that masks US numbers as "(XXX) XXX-XXXX" while the broker
// types (the same behavior as the PMS admin portal's PhoneInput). Controlled to
// match the rest of the portal's fields: the parent owns the formatted value and
// `onChange` reports it formatted. The display is normalized on every render via
// maskPhoneInput, so a seeded/messy value (e.g. "619.555.0100") shows clean —
// except a non-US value, which is shown verbatim rather than reshaped into a
// number nobody typed (BRK-S073). It's the phone sibling of TextField (label +
// inline error wired for a11y).
//
// BRK-S073 adds an OPTIONAL line-type select beside the number, offering the PMS
// enum verbatim. It appears only when `onLineTypeChange` is passed — the scalar
// single-phone fields (account contacts, the broker's own profile) don't carry a
// line type. Unset is always valid: PMS does no carrier lookup, so "unknown" is
// a legitimate state and the portal never guesses.
interface PhoneFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  id?: string;
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  /** The row's line type ("" when unset). Ignored without onLineTypeChange. */
  lineType?: PhoneLineType | "";
  /** Pass to render the line-type select; omit for a number-only field. */
  onLineTypeChange?: (value: PhoneLineType | "") => void;
  /** Label for the line-type select — indexed by the caller when a list of
   *  numbers would otherwise repeat the same label (PhoneListField). */
  lineTypeLabel?: string;
}

export function PhoneField({
  label,
  value,
  onChange,
  error,
  id,
  required,
  optional,
  placeholder,
  lineType = "",
  onLineTypeChange,
  lineTypeLabel = "Line type",
}: PhoneFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const lineTypeId = `${fieldId}-line-type`;

  const numberField = (
    <div className="flex w-full flex-col gap-1 text-left">
      <span className="flex items-baseline gap-1.5">
        <label
          htmlFor={fieldId}
          className={error ? FIELD_LABEL_ERROR : FIELD_LABEL}
        >
          {label}
        </label>
        <FieldMarker required={required} optional={optional} />
      </span>
      <input
        id={fieldId}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        required={required}
        placeholder={placeholder ?? "(555) 555-5555"}
        value={maskPhoneInput(value)}
        onChange={(e) => onChange(maskPhoneInput(e.target.value))}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={FIELD_CONTROL}
      />
      {error && (
        <p id={errorId} className={FIELD_ERROR}>
          {error}
        </p>
      )}
    </div>
  );

  if (!onLineTypeChange) return numberField;

  // Number + line type: stacked on mobile, side by side from sm up, with the
  // select held to a fixed share so the number keeps the room it needs.
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
      {numberField}
      <div className="flex w-full flex-col gap-1 text-left sm:w-44 sm:shrink-0">
        <span className="flex items-baseline gap-1.5">
          <label htmlFor={lineTypeId} className={FIELD_LABEL}>
            {lineTypeLabel}
          </label>
          <FieldMarker optional />
        </span>
        <select
          id={lineTypeId}
          value={lineType}
          onChange={(e) =>
            onLineTypeChange(e.target.value as PhoneLineType | "")
          }
          className={FIELD_CONTROL}
        >
          <option value="">Unknown</option>
          {PHONE_LINE_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
